import { useEffect, useMemo, useRef } from 'react';

import { useOrganizationContext } from '@apps/web/context/organization_provider';
import { scheduleAutoSwitchToast } from '@apps/web/lib/auto_switch_toast';
import { modeStore, useMode } from '@apps/web/lib/mode_store';
import { type Mode } from '@libraries/typescript/api_client';

type UseDeepLinkOrganizationResult = {
    readonly canSwitchToDeepLinkOrganization: boolean;
    readonly effectiveOrganizationId: string | null;
    readonly isWaitingForDeepLinkOrganization: boolean;
};

export function useDeepLinkOrganization(
    deepLinkOrganizationId: string | null | undefined,
    deepLinkMode?: Mode | null,
): UseDeepLinkOrganizationResult {
    const { organizationId, memberships, isLoading, setOrganizationId } =
        useOrganizationContext();
    const mode = useMode();
    const handledDeepLinkKeyRef = useRef<string | null>(null);
    const pendingModeToastRef = useRef<Mode | null>(null);
    const initialDeepLinkOrganizationIdRef = useRef(deepLinkOrganizationId);
    const initialDeepLinkModeRef = useRef(deepLinkMode);
    const deepLinkKey =
        deepLinkOrganizationId || deepLinkMode
            ? `${deepLinkOrganizationId ?? ''}:${deepLinkMode ?? ''}`
            : null;
    const hasDeepLinkOrganizationMembership =
        !!deepLinkOrganizationId &&
        memberships.some(
            (membership) =>
                membership.organization_id === deepLinkOrganizationId,
        );
    const hasHandledDeepLink =
        !!deepLinkKey && handledDeepLinkKeyRef.current === deepLinkKey;
    const shouldInitializeDeepLink = !!deepLinkKey && !hasHandledDeepLink;
    const shouldSwitchToDeepLinkMode =
        shouldInitializeDeepLink &&
        !!deepLinkMode &&
        deepLinkMode !== mode &&
        (!deepLinkOrganizationId || hasDeepLinkOrganizationMembership);
    const isDeepLinkModeReady = !deepLinkMode || deepLinkMode === mode;
    const needsDeepLinkOrganizationSwitch =
        shouldInitializeDeepLink &&
        hasDeepLinkOrganizationMembership &&
        !!deepLinkOrganizationId &&
        organizationId !== deepLinkOrganizationId;
    const deepLinkOrganizationName = useMemo(
        () =>
            memberships.find(
                (membership) =>
                    membership.organization_id === deepLinkOrganizationId,
            )?.organization_name ?? null,
        [deepLinkOrganizationId, memberships],
    );

    const canSwitchToDeepLinkOrganization =
        shouldInitializeDeepLink &&
        isDeepLinkModeReady &&
        hasDeepLinkOrganizationMembership;
    // The deep-link org carried in the URL when this tab first rendered. The
    // Chrome extension "Find caregivers" link opens a brand-new tab already
    // pointed at the shift's org, so aligning to that org is the tab's
    // initialization, not a switch the user made -- announcing "Switched to
    // ..." there is the confusing behavior reported in PHO-12034. A deep link
    // to this org that instead arrives later via in-tab navigation means the
    // user was already working in another org here and is genuinely moved, so
    // that still announces.
    const isInitialTabDeepLinkOrganization =
        !!deepLinkOrganizationId &&
        deepLinkOrganizationId === initialDeepLinkOrganizationIdRef.current;
    // Same reasoning as the org guard for the mode-only announcement: a fresh
    // tab that opens already pointed at this mode is initializing, not
    // switching. The "Find caregivers" flow forces live mode for the shift,
    // so a tab last left in sandbox would otherwise announce "Switched to live
    // mode ..." on open even though the user changed nothing (PHO-12034). A
    // mode that only appears via later in-tab navigation is a real switch.
    const isInitialTabDeepLinkMode =
        !!deepLinkMode && deepLinkMode === initialDeepLinkModeRef.current;
    const effectiveOrganizationId = canSwitchToDeepLinkOrganization
        ? deepLinkOrganizationId
        : organizationId;
    const isWaitingForDeepLinkOrganization =
        shouldSwitchToDeepLinkMode ||
        (shouldInitializeDeepLink &&
            !!deepLinkOrganizationId &&
            hasDeepLinkOrganizationMembership &&
            (isLoading ||
                !isDeepLinkModeReady ||
                (canSwitchToDeepLinkOrganization &&
                    organizationId !== deepLinkOrganizationId)));

    useEffect(() => {
        if (!deepLinkKey || hasHandledDeepLink) {
            return;
        }

        if (
            !isLoading &&
            deepLinkOrganizationId &&
            !hasDeepLinkOrganizationMembership
        ) {
            pendingModeToastRef.current = null;
            handledDeepLinkKeyRef.current = deepLinkKey;
            return;
        }

        const isOrganizationReady =
            !deepLinkOrganizationId ||
            organizationId === deepLinkOrganizationId;
        if (!isLoading && isDeepLinkModeReady && isOrganizationReady) {
            pendingModeToastRef.current = null;
            handledDeepLinkKeyRef.current = deepLinkKey;
        }
    }, [
        deepLinkKey,
        deepLinkOrganizationId,
        hasDeepLinkOrganizationMembership,
        hasHandledDeepLink,
        isDeepLinkModeReady,
        isLoading,
        organizationId,
    ]);

    useEffect(() => {
        if (!shouldSwitchToDeepLinkMode || !deepLinkMode) {
            return;
        }

        if (needsDeepLinkOrganizationSwitch) {
            pendingModeToastRef.current = deepLinkMode;
        } else {
            pendingModeToastRef.current = null;
            modeStore.setMode(deepLinkMode);
            if (!isInitialTabDeepLinkMode) {
                scheduleAutoSwitchToast({ mode: deepLinkMode });
            }
            return;
        }

        modeStore.setMode(deepLinkMode);
    }, [
        deepLinkMode,
        isInitialTabDeepLinkMode,
        needsDeepLinkOrganizationSwitch,
        shouldSwitchToDeepLinkMode,
    ]);

    useEffect(() => {
        if (
            !isDeepLinkModeReady ||
            !deepLinkOrganizationId ||
            isLoading ||
            !canSwitchToDeepLinkOrganization ||
            organizationId === deepLinkOrganizationId
        ) {
            return;
        }

        setOrganizationId(deepLinkOrganizationId);
        if (!isInitialTabDeepLinkOrganization) {
            scheduleAutoSwitchToast({
                organizationName: deepLinkOrganizationName,
                mode: pendingModeToastRef.current,
            });
        }
        pendingModeToastRef.current = null;
    }, [
        canSwitchToDeepLinkOrganization,
        deepLinkOrganizationId,
        deepLinkOrganizationName,
        isDeepLinkModeReady,
        isInitialTabDeepLinkOrganization,
        isLoading,
        organizationId,
        setOrganizationId,
    ]);

    return {
        canSwitchToDeepLinkOrganization,
        effectiveOrganizationId,
        isWaitingForDeepLinkOrganization,
    };
}
